import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Plus, Minus, Shield, Wrench, Target,
  Activity, Cpu, BarChart3, Package, MessageSquare, Scale
} from "lucide-react";
import CognaraReasoningStrata from "../components/CognaraReasoningStrata";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(end * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ═══════ DATA ═══════ */
const DOMAINS = [
  { n: 1, name: "Contracts & Claims" },
  { n: 2, name: "Engineering & Technical" },
  { n: 3, name: "Project Controls" },
  { n: 4, name: "Procurement & Supply" },
  { n: 5, name: "Governance & Communication" },
];

const AGENTS = [
  { domain: 1, slug: "claims-contracts", name: "Claims & Contracts Agent", Icon: Scale, body: "FIDIC/NEC clause interpretation, claim drafting with clause citations, counter-claim analysis, delay analysis, arbitration precedent retrieval. Trained on 50,000+ annotated clauses and 8,000+ precedents." },
  { domain: 1, slug: "contract-intel", name: "Contract Intelligence Agent", Icon: Shield, body: "Clause-level risk scoring, missing clause detection, conflict detection within contracts, obligation extraction, variation order assessment. Benchmarks against FIDIC/NEC/Indian standard norms." },
  { domain: 2, slug: "technical-engineering", name: "Technical Engineering Agent", Icon: Wrench, body: "P&ID reading, equipment sizing, code compliance (API/ASME/IS), HAZOP/LOPA guidance, specification drafting. 12,000+ standards indexed by application." },
  { domain: 2, slug: "process-operations", name: "Process & Operations Agent", Icon: Target, body: "Process simulation interpretation, operations readiness, commissioning planning, performance testing protocols, operating manual drafting." },
  { domain: 3, slug: "schedule-delay", name: "Schedule & Delay Agent", Icon: Activity, body: "CPM analysis, forensic schedule analysis (TIA per AACE RP 52R-06), Monte Carlo forecasting, critical-path diagnosis, concurrent delay methodology (Malmaison, SCL Protocol)." },
  { domain: 3, slug: "cost-commercial", name: "Cost & Commercial Agent", Icon: BarChart3, body: "EVM analysis (CPI/SPI/EAC/ETC), should-cost modeling, escalation analysis, bid evaluation, cost forecasting, LD/bonus calculations. Based on 10,000+ project post-mortems." },
  { domain: 4, slug: "procurement", name: "Procurement & Supply Chain Agent", Icon: Package, body: "Category strategy, supplier risk analysis, sourcing event design, material planning, expediting logic, insolvency mitigation planning." },
  { domain: 4, slug: "hse-regulatory", name: "HSE & Regulatory Agent", Icon: Shield, body: "OSHA/BOCW/PESO compliance, HIRA/JSA drafting, incident classification, regulatory filing guidance, environmental clearance, CPCB/SPCB norms." },
  { domain: 5, slug: "stakeholder-comms", name: "Stakeholder & Communication Agent", Icon: MessageSquare, body: "Board-level communication, stakeholder updates, meeting synthesis, executive summaries, presentation generation, weekly/monthly reporting." },
  { domain: 5, slug: "orchestrator", name: "Cross-Functional Orchestrator", Icon: Cpu, body: "Routes complex multi-domain queries to the right specialist agents, synthesizes responses spanning contracts + schedule + cost + procurement into a single coherent cited answer." },
];

const CONVERSATIONS = {
  claims: { user: "Our client issued a notice under FIDIC Yellow Book Sub-Clause 8.4 disputing our 62-day EOT for monsoon-impacted civil works. They cite 'foreseeable weather'. Our position?", agent: `Three angles to build your position:

1. **Foreseeability test under FIDIC Yellow 2017** requires 'experienced contractor' standard (Sub-Clause 8.4(c)). IMD data: September 2024 rainfall was 287mm vs 10-year mean of 156mm — 84% deviation. Exceeds the 50% threshold for 'unforeseeable' (HCC v. NHAI, 2019).

2. **Check Particular Conditions 8.4 amendments** — if Client hasn't narrowed 'Exceptional Events', default General Conditions apply.

3. **Concurrent delay carve-out** — Sub-Clause 8.5 allows EOT even with concurrent delays provided monsoon was on CP.

**Recommended:** Acknowledge within 14 days (20.2.1), provide IMD data, reference CP impact, request pre-arbitration meeting (21.1).

**Precedent:** ONGC v. Saw Pipes (2003) 5 SCC 705; Kailash Nath v. DDA (2015) 4 SCC 136.`, meta: "23 sources · 4 precedents · 11.2s" },
  technical: { user: "Specify a centrifugal compressor for natural gas gathering. 50 MMSCFD, 35 bar suction, 70 bar discharge, 40°C.", agent: `**Codes:** API 617 9th Ed, API 614 (lube/seals), ASME PTC 10, NACE MR0175/ISO 15156 if sour, IS 10431.

**Design:** Compression ratio 2.0 — single-stage feasible. Polytropic head ~95 kJ/kg. Driver: gas turbine (Solar Titan/Centaur) for remote; electric if grid reliable. Anti-surge critical for variable upstream flow. Dry gas seals (API 614 Plan 74). Vibration per API 670.

**Vendors:** Siemens Energy, Baker Hughes (Nuovo Pignone), MAN, Kobelco.

**Red flags:** Surge margin <10%, single-source spares, missing NACE compliance if sour service.

Want me to draft the TDS per API 617 Annex C?`, meta: "18 standards · 9.8s" },
  schedule: { user: "Forensic delay analysis: TIA shows 47 days owner-caused delay (late drawings), 31 days concurrent contractor delay. How to handle?", agent: `**Four accepted methodologies:**
1. **Malmaison (dominant in India):** Full 47-day EOT even with concurrent own-delay, IF owner-delay is on CP.
2. **Apportionment:** ~28 days. Rare in Indian infra.
3. **'But-for' test:** If owner's delay alone sufficient, full EOT.
4. **SCL Protocol 2nd Ed:** Full EOT, no prolongation for concurrent period.

**Indian jurisprudence favors Malmaison:** Dyna Technologies v. Crompton Greaves (2019) SC.

**Recommendation:** Claim 47-day EOT (Sub-Clause 8.4). Prolongation costs only for net owner-attributable (~16-20 days). Apply TIA per AACE RP 52R-06.

**Red flag:** Check 28-day notice compliance (Sub-Clause 20.2.1).`, meta: "12 precedents · AACE RP 52R-06 · 13.6s" },
  cost: { user: "CPI dropped from 1.02 to 0.94 in 6 weeks on a ₹3,200 Cr EPC project. Root cause?", agent: `At CPI 0.94, you're spending ₹1.064 for every ₹1 earned. EAC projects ₹3,404 Cr — ₹204 Cr overrun.

**Root causes (from 10,000+ post-mortems):** Scope creep without VO (42%), Rework (28%), Productivity decline (18%), Rate escalation (12%).

**Check:** Has SPI moved in parallel? Compare CBS drill-down (Labour vs Material vs Subcontract). Review change order register for pending VOs.

**Per AACE RP 42R-08:** When CPI <0.95, recovery probability <15% without intervention. Recommend immediate cost review workshop.`, meta: "AACE RP 42R-08 · 10K+ post-mortems · 8.2s" },
  orchestrator: { user: "₹4,200 Cr refinery: contested ₹47 Cr claim, slipping schedule, key fabrication vendor missed third milestone. Integrated assessment?", agent: `Multi-agent synthesis (Claims + Schedule + Procurement + Cost):

**1. Claim (Claims Agent):** Concurrent delay likely. Malmaison approach → defensible position ₹18-25 Cr (40-55% of claimed).

**2. Schedule (Schedule Agent):** At SPI 0.97, Monte Carlo forecasts P80 at Week 168 vs planned 156 — 12-week overrun. Recovery costs 8-12% premium.

**3. Vendor (Procurement Agent):** Three misses = chronic non-performance. Options: cure notice (30-day remedy), parallel alt fabricator, partial termination. Re-sourcing premium: 15-20%.

**4. Financial (Cost Agent):** Combined risk: ₹73-105 Cr (1.7-2.5% of BAC). Recoverable if acted within 4 weeks.

**Immediate actions:** (1) Cure notice to vendor THIS WEEK, (2) Engage claims consultant, (3) Schedule recovery study, (4) Board briefing on ₹73-105 Cr risk envelope.`, meta: "4 agents synthesized · 27 sources · 16.4s" },
};

const FAQS = [
  { q: "Is Cognara just ChatGPT or Claude with a capital-projects prompt?", a: "No. It uses Anthropic Claude as foundation but adds: (1) a 2.4 billion-token domain corpus indexed via MongoDB Atlas Vector Search, (2) multi-agent orchestration with 9 specialists, (3) continuous calibration against real outcomes, (4) citation and precedent retrieval that general-purpose AI lacks." },
  { q: "What prevents Cognara from hallucinating?", a: "Three layers: (1) RAG — responses grounded in retrieved documents with citations; (2) confidence scoring — flags low-confidence answers; (3) continuous validation. For critical applications, produces cited, traceable reasoning chains." },
  { q: "How does Cognara handle confidential project data?", a: "Three modes: (1) Shared cloud — sensitive data auto-redacted/tokenized; (2) Dedicated cloud — full tenant isolation, customer-managed encryption; (3) On-premise — all data and inference local with self-hosted LLMs." },
  { q: "Can it be trained on our organization's data?", a: "Yes. RAG integration (your contracts, standards, SOPs) is standard in Enterprise tier. Fine-tuning on dedicated model available for Defense tier, typically on-premise." },
  { q: "What languages does Cognara support?", a: "Native English and Hindi. Limited: Gujarati, Tamil, Bengali, Marathi, Kannada. Global: French, Spanish, Arabic, Chinese on Enterprise tier." },
  { q: "How accurate on legal questions?", a: "94% on FIDIC/NEC contract review vs 87% for senior legal counsel. NOT a replacement for qualified counsel on high-stakes litigation — augments legal teams, reduces review time 10-20×." },
  { q: "Does Cognara replace our consultants?", a: "No — augments them. 30-50% reduction in research/drafting time, freeing senior consultants for judgment, strategy, and client relationships." },
  { q: "What's the integration surface?", a: "REST APIs, webhooks, team chat tools bots, AI copilots, and native integrations with enterprise ERP S/4, document libraries, legacy schedulers, scheduling tools, enterprise procurement suites, Autodesk-class CAD/BIM APS." },
  { q: "How often is Cognara updated?", a: "Foundation model: when Anthropic releases updates. Corpus: monthly (regulatory), quarterly (codes/standards), continuously (post-mortems). Customer-specific corpora on request." },
  { q: "Typical deployment timeline?", a: "Professional: 48 hours. Enterprise: 4-8 weeks (dedicated cloud + custom corpus). Defense: 10-16 weeks (on-premise + fine-tuning + security validation)." },
  { q: "Who uses it inside my organization?", a: "Contracts/Legal, Technical leads, Project Controls, Procurement, HSE, Executives. Typical adoption: 40-60% of white-collar project staff within 12 months." },
  { q: "Can we try before committing?", a: "Yes. Professional: 30-day trial, 10 users. Enterprise: 60-day pilot on 1-2 use cases. Defense: on-site PoC with Capexara engineers for 2-3 weeks." },
];

/* ═══════ PAGE ═══════ */
export default function PlatformSutradhar() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Agents />
      <Section4Cockpit />
      <Section5Cognitive />
      <Section6Integrations />
      <Section7Industries />
      <Section8Outcomes />
      <Section9Faq />
      <Section10Cta />
    </>
  );
}

/* ═══════ S1 HERO — grantha-hero-grid ═══════ */
function Section1Hero() {
  return (
    <section className="grantha-hero" data-testid="st-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="st-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono">Platforms / AI-Powered Consulting / COGNARA</div>
          <div className="eyebrow" style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>Generative AI Agent for Capital Projects</span>
          </div>
          <h1 className="grantha-h1" data-testid="st-h1">
            <span className="grantha-indo st-logo-lockup">
              <span className="st-icon-axis"><span className="st-hline" /><span className="st-hline" /><span className="st-hline" /><span className="st-vline" /><span className="st-dot" /><span className="st-dot" /><span className="st-dot" /></span>
              <span className="st-wordmark">Cognara</span>
            </span>
            <span className="grantha-h1-en">The 30-year consultant, <em>always on call.</em></span>
          </h1>
          <p className="grantha-sub" data-testid="st-hero-sub">
            Trained on FIDIC, NEC, API, ASME, IS codes, BOCW, CPCB, GST, CII best practices, 50,000+ annotated clauses, 10,000+ post-mortems from failed megaprojects, and the hard-won intuition of engineers who've commissioned refineries at 3 AM. Not a chatbot — a multi-agent reasoning system.
          </p>
          <div className="grantha-ctas">
            <Link to="/contact?p=cognara" className="btn btn-primary" data-testid="st-cta-demo">Ask Cognara a question <span className="arrow">→</span></Link>
            <a href="#st-cockpit" className="btn btn-ghost">See live conversations</a>
          </div>
          <div className="grantha-trust mono">
            2.4B tokens domain corpus · 9 specialist agents · Claude + MongoDB Atlas Vector Search · ISO 27001
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="st-hero-viz">
          <CognaraReasoningStrata />
        </div>
      </div>
    </section>
  );
}

/* Agent constellation SVG — matches vault/hexagon pattern */
function AgentConstellationViz() {
  return (
    <svg viewBox="0 0 560 560" className="grantha-vault" aria-label="Multi-agent AI system visualization">
      <defs>
        <linearGradient id="stGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#2E70AD" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      <circle cx="280" cy="280" r="220" fill="#1E4E8C" fillOpacity="0.03" />
      <circle cx="280" cy="280" r="180" fill="none" stroke="#1E4E8C" strokeOpacity="0.1" strokeDasharray="3 8" style={{ transformOrigin: "280px 280px", animation: "orbitSpin 50s linear infinite" }} />

      {/* Central orchestrator */}
      <g style={{ transformOrigin: "280px 280px", animation: "vaultFloat 6s ease-in-out infinite" }}>
        <polygon points="280,190 350,230 350,330 280,370 210,330 210,230" fill="url(#stGrad)" stroke="#1E4E8C" strokeWidth="1.5" strokeOpacity="0.8" />
        <polygon points="280,190 350,230 280,280 210,230" fill="#1E4E8C" fillOpacity="0.15" stroke="#1E4E8C" strokeWidth="1" strokeOpacity="0.5" />
        <text x="280" y="270" textAnchor="middle" fontSize="10" fill="#1E4E8C" fontFamily="var(--f-mono)" fontWeight="700" letterSpacing="1">ORCHESTRATOR</text>
        <text x="280" y="290" textAnchor="middle" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">9 AGENTS</text>
        {/* Brain lines */}
        <rect x="245" y="305" width="70" height="6" rx="2" fill="#1E4E8C" fillOpacity="0.25" />
        <rect x="255" y="316" width="50" height="5" rx="2" fill="#2E70AD" fillOpacity="0.15" />
        <rect x="248" y="326" width="64" height="5" rx="2" fill="#2E70AD" fillOpacity="0.15" />
      </g>

      {/* 9 specialist agent nodes */}
      {[
        { x: 400, y: 100, label: "CLAIMS" },
        { x: 480, y: 220, label: "TECH" },
        { x: 460, y: 380, label: "SCHED" },
        { x: 340, y: 480, label: "COST" },
        { x: 220, y: 480, label: "PROCUR" },
        { x: 100, y: 380, label: "HSE" },
        { x: 80, y: 220, label: "PROCESS" },
        { x: 160, y: 100, label: "COMMS" },
        { x: 280, y: 80, label: "CORPUS" },
      ].map((n, i) => (
        <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.2}s infinite` }}>
          <line x1="280" y1="280" x2={n.x} y2={n.y} stroke="#2E70AD" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="2 4" />
          <circle cx={n.x} cy={n.y} r="22" fill="var(--bg-elev)" stroke="#1E4E8C" strokeWidth="1.5" />
          <text x={n.x} y={n.y + 3} textAnchor="middle" fontFamily="var(--f-mono)" fontSize="7" fontWeight="700" fill="#1E4E8C">{n.label}</text>
          <circle r="2.5" fill="#F26B1F" style={{ offsetPath: `path('M 280 280 L ${n.x} ${n.y}')`, animation: `pipeFlow ${3 + i * 0.3}s linear infinite`, animationDelay: `${i * 0.4}s` }} />
        </g>
      ))}

      <text x="280" y="530" textAnchor="middle" fontFamily="var(--f-mono)" fontSize="9" fill="#F26B1F" letterSpacing="2" fontWeight="700">2.4B TOKENS · 50K CLAUSES · 10K POST-MORTEMS</text>
    </svg>
  );
}

/* ═══════ S2 PROBLEM — grantha-problem ═══════ */
function Section2Problem() {
  return (
    <section className="grantha-problem" data-testid="st-problem">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The problem with general AI</div>
        <h2 className="grantha-problem-h">
          <em>General-purpose AI can write a poem about a refinery. It cannot tell you if your LD clause is defensible under Indian contract law.</em>
        </h2>
        <div className="grantha-problem-grid">
          {[
            { n: 94, suffix: "%", label: "risk clause detection by Cognara vs 87% avg for senior legal counsel (15+ yr experience)" },
            { n: 20, suffix: "×", label: "faster than manual review — 8 minutes per contract vs 3.4 hours by lawyers" },
            { n: 2.4, suffix: "B", label: "tokens in the domain corpus — contracts, codes, standards, regulations, post-mortems", decimals: 1 },
            { n: 9, label: "specialist agents — Claims, Technical, Schedule, Cost, Procurement, HSE, Process, Comms, Orchestrator" },
          ].map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`st-stat-${i}`}>
              <div className="grantha-problem-num"><CountUp end={s.n} suffix={s.suffix || ""} decimals={s.decimals || 0} /></div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote">
          Domain AI isn't ChatGPT with a capital-projects prompt. <em>It's a different category of system.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 AGENTS — grantha-arch layer-tab ═══════ */
function Section3Agents() {
  const [activeDomain, setActiveDomain] = useState(1);
  return (
    <section className="grantha-arch" data-testid="st-agents">
      <div className="container">
        <div className="eyebrow">Agent architecture</div>
        <h2 className="grantha-arch-h">Not one agent — a team. <em>Each agent, a specialist.</em></h2>
        <p className="grantha-arch-sub">
          Cognara is a coordinated team of 9 specialist agents plus a cross-functional orchestrator. Complex queries invoke multiple agents in parallel; the orchestrator synthesizes their reasoning into a single, coherent, cited response.
        </p>
        <div className="grantha-layers" data-testid="st-domains">
          {DOMAINS.map((D) => (
            <button key={D.n} type="button" className={"grantha-layer " + (activeDomain === D.n ? "is-active" : "")} onMouseEnter={() => setActiveDomain(D.n)} onClick={() => setActiveDomain(D.n)} data-testid={`st-domain-${D.n}`}>
              <span className="grantha-layer-n mono">DOMAIN · 0{D.n}</span>
              <span className="grantha-layer-name">{D.name}</span>
              <span className="grantha-layer-count mono">{AGENTS.filter(a => a.domain === D.n).length}</span>
            </button>
          ))}
        </div>
        <div className="grantha-modules" data-testid="st-agent-grid">
          {AGENTS.filter(a => a.domain === activeDomain).map((a) => (
            <div key={a.slug} className="grantha-module" data-testid={`st-agent-${a.slug}`}>
              <div className="grantha-module-icon"><a.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{a.name}</div>
                <div className="grantha-module-desc">{a.body}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "var(--f-serif)", fontStyle: "italic", fontSize: 18, color: "var(--text-2)", textAlign: "center", marginTop: 40 }}>
          Complex queries invoke multiple agents in parallel. Cognara synthesizes their reasoning into a single, coherent, cited response.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S4 COCKPIT — grantha-viewer-grid (conversation mockup left, features right) ═══════ */
function Section4Cockpit() {
  const [activeTab, setActiveTab] = useState("claims");
  const tabs = [
    { id: "claims", label: "Claims" },
    { id: "technical", label: "Technical" },
    { id: "schedule", label: "Schedule" },
    { id: "cost", label: "Cost" },
    { id: "orchestrator", label: "Cross-Func" },
  ];
  const conv = CONVERSATIONS[activeTab];

  return (
    <section className="grantha-viewer" id="st-cockpit" data-testid="st-cockpit">
      <div className="container grantha-viewer-grid">
        <div className="grantha-viewer-visual">
          <div className="grantha-viewer-frame">
            <div className="grantha-viewer-bar">
              <span className="mono" style={{ color: "#F26B1F" }}>✨ Cognara</span>
              <span className="mono">Agent: {tabs.find(t => t.id === activeTab)?.label}</span>
            </div>
            <div className="st-tabs" style={{ padding: "0 12px", background: "var(--bg-sunk)", borderBottom: "1px solid var(--border)" }}>
              {tabs.map((t) => (
                <button key={t.id} type="button" className={"st-tab " + (activeTab === t.id ? "active" : "")} onClick={() => setActiveTab(t.id)} data-testid={`st-tab-${t.id}`}>{t.label}</button>
              ))}
            </div>
            <div className="st-chat-body" style={{ maxHeight: 340 }}>
              <div className="st-msg st-msg-user">{conv.user}</div>
              <div className="st-msg st-msg-agent">
                <span className="st-agent-spark">⚡</span>
                <div className="st-mono" style={{ whiteSpace: "pre-wrap" }}>{conv.agent}</div>
              </div>
              <div className="st-msg-meta mono">⚡ {conv.meta}</div>
            </div>
            <div className="grantha-viewer-meta mono">
              <div>9 specialist agents · Cognara Orchestrator</div>
              <div style={{ color: "var(--ember)" }}>Cited. Traceable. Audit-ready.</div>
            </div>
          </div>
        </div>
        <div className="grantha-viewer-copy">
          <div className="eyebrow">Live agent conversations</div>
          <h2 className="grantha-viewer-h"><em>Domain mastery,</em> demonstrated live.</h2>
          <div className="grantha-features">
            {[
              { n: "01", t: "FIDIC/NEC clause mastery", b: "Reads contracts like senior counsel — clause citations, precedent retrieval, risk scoring, counter-claim angles." },
              { n: "02", t: "API/ASME/IS code fluency", b: "Sizes equipment, checks compliance, drafts specifications — referencing 12,000+ indexed technical standards." },
              { n: "03", t: "Forensic schedule analysis", b: "TIA per AACE RP 52R-06, concurrent delay (Malmaison), Monte Carlo forecasting, CP diagnosis." },
              { n: "04", t: "Multi-agent synthesis", b: "Complex queries spanning contracts + schedule + cost + procurement → one coherent, cited response in under 60 seconds." },
              { n: "05", t: "Real precedent citations", b: "ONGC v. Saw Pipes (2003), Kailash Nath v. DDA (2015), Dyna Technologies (2019) — real cases, not fictional references." },
            ].map((f) => (
              <div className="grantha-feature" key={f.n}>
                <span className="grantha-feature-n mono">{f.n}</span>
                <div>
                  <div className="grantha-feature-t">{f.t}</div>
                  <div className="grantha-feature-b">{f.b}</div>
                </div>
              </div>
            ))}
          </div>
          <blockquote className="grantha-pull">
            This is not generic AI. <em>This is what domain mastery looks like.</em>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S5 COGNITIVE — grantha-wf dark band ═══════ */
function Section5Cognitive() {
  const layers = [
    { name: "Contracts", count: "50,000+ clauses", detail: "FIDIC, NEC, JCT, AIA, Indian Contract Act, 8,000+ arbitration precedents" },
    { name: "Engineering", count: "12,000+ standards", detail: "API, ASME, IS, IEC, IEEE, NFPA, ISO 9001/14001/45001" },
    { name: "Regulatory", count: "400+ frameworks", detail: "BOCW, CPCB, PESO, OISD, GST Act, OSHA, REACH, DSEAR" },
    { name: "PM Methodology", count: "200+ practices", detail: "PMI PMBOK 7, CII Best Practices, AACE RPs, AWP, Last Planner" },
    { name: "Commercial", count: "10-year indices", detail: "RS Means, EVM standards, should-cost, WPI/CPI, escalation" },
    { name: "Post-mortems", count: "10,000+ projects", detail: "Failed megaprojects, IPA studies, CAG/GAO audits, Oxford research" },
    { name: "India-specific", count: "30+ years", detail: "PSU archives, IIT/NIT research, state-level patterns, Hindi/English" },
  ];
  return (
    <section className="grantha-wf" data-testid="st-corpus">
      <div className="container grantha-wf-grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--steel-200)" }}>The training corpus</div>
          <h2 className="grantha-wf-h">2.4 billion tokens of capital-project knowledge — <em>ingested, structured, indexed.</em></h2>
          <p className="grantha-wf-body">Not scraped. Not synthetic. Curated over 18 months by Capexara's consulting leadership, with ongoing expert review. Seven corpus domains:</p>
          <ul className="grantha-wf-list">
            {layers.map((l, i) => <li key={i}><strong>{l.name}</strong> — {l.count}. {l.detail}</li>)}
          </ul>
        </div>
        <div className="grantha-wf-diagram">
          <svg viewBox="0 0 360 400" style={{ width: "100%", maxWidth: 360, margin: "0 auto", display: "block" }}>
            {layers.map((l, i) => {
              const y = 30 + i * 50;
              const w = 210 - i * 10;
              return (
                <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.2}s infinite` }}>
                  <rect x={(360 - w) / 2} y={y} width={w} height="36" rx="6" fill={`rgba(30,78,140,${0.08 + i * 0.02})`} stroke="#1E4E8C" strokeOpacity={0.3 + i * 0.05} strokeWidth="1" />
                  <text x="180" y={y + 16} textAnchor="middle" fontSize="8" fontWeight="600" fill="#6ac8ff" fontFamily="var(--f-mono)">{l.name.toUpperCase()}</text>
                  <text x="180" y={y + 28} textAnchor="middle" fontSize="7" fill="rgba(243,244,246,0.5)" fontFamily="var(--f-mono)">{l.count}</text>
                </g>
              );
            })}
            <text x="180" y="390" textAnchor="middle" fontSize="8" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1" fontWeight="700">CONTINUOUSLY UPDATED</text>
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S6 INTEGRATIONS — grantha-integ ═══════ */
function Section6Integrations() {
  return (
    <section className="grantha-integ" data-testid="st-integrations">
      <div className="container">
        <div className="eyebrow">Deployment &amp; integration</div>
        <h2 className="grantha-integ-h">Three deployment models. <em>Enterprise-grade security by default.</em></h2>
        <div className="grantha-integ-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
          <div className="grantha-integ-col">
            <div className="grantha-integ-head mono">Shared Cloud</div>
            <ul className="grantha-integ-list">
              <li>Azure India (Mumbai, Hyderabad)</li><li>Full tenant isolation · ISO 27001</li><li>Onboarding: 48 hours</li><li>Best for: pilots, consulting engagements</li>
            </ul>
          </div>
          <div className="grantha-integ-col">
            <div className="grantha-integ-head mono">Dedicated Cloud</div>
            <ul className="grantha-integ-list">
              <li>Customer-owned Azure/AWS</li><li>Single-tenant, geo-pinned, BYOK encryption</li><li>HIPAA/PCI/GDPR ready</li><li>Onboarding: 4-8 weeks</li>
            </ul>
          </div>
          <div className="grantha-integ-col">
            <div className="grantha-integ-head mono">On-Premise / Air-Gapped</div>
            <ul className="grantha-integ-list">
              <li>Self-hosted LLM (Llama, Mistral)</li><li>No external API calls · Air-gapped</li><li>Defense / Nuclear / PSU ready</li><li>Onboarding: 10-16 weeks</li>
            </ul>
          </div>
        </div>
        <div style={{ marginTop: 32 }}>
          <div className="grantha-integ-head mono" style={{ marginBottom: 12 }}>Native integrations</div>
          <div className="grantha-integ-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
            {[
              { head: "ERP & Project", items: ["enterprise ERP · procurement suites", "legacy schedulers · scheduling tools", "enterprise ERP · Unifier", "Autodesk-class CAD/BIM BIM stack"] },
              { head: "Collaboration", items: ["Slack · Microsoft Teams", "document libraries · Google Drive", "document libraries · Notion", "Email integration"] },
              { head: "Capexara Native", items: ["Panorix (Control Tower)", "ProcureX (Procurement)", "Docuweave (Documents)", "All 7 platforms"] },
              { head: "APIs", items: ["REST APIs · Webhooks", "AI copilots", "Custom integrations", "SSO (OAuth2 / Entra ID)"] },
            ].map((g, i) => (
              <div className="grantha-integ-col" key={i}>
                <div className="grantha-integ-head mono">{g.head}</div>
                <ul className="grantha-integ-list">{g.items.map(it => <li key={it}>{it}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S7 INDUSTRIES — grantha-inds ═══════ */
function Section7Industries() {
  return (
    <section className="grantha-inds" data-testid="st-industries">
      <div className="container">
        <div className="eyebrow">Industries served</div>
        <h2 className="grantha-inds-h">Deployed across <em>every capital-project industry</em> we serve.</h2>
        <div className="grantha-chips">
          {["Oil, Gas & Energy — Upstream · Downstream · LNG · Hydrogen", "Power & Utilities — Generation · T&D · BESS · Renewables", "Chemicals — Basic · Specialty · Agrochemicals", "Infrastructure — Roads · Metros · Data Centers · Bridges", "Metals, Mining & Minerals", "Manufacturing — Discrete · Life Sciences", "Marine — Ports · Shipbuilding"].map(c => <Link to="/industries" className="grantha-chip" key={c}>{c}</Link>)}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S8 OUTCOMES — grantha-out ═══════ */
function Section8Outcomes() {
  return (
    <section className="grantha-out" data-testid="st-outcomes">
      <div className="container">
        <div className="eyebrow">Measured impact</div>
        <h2 className="grantha-out-h">How Cognara measures up — against lawyers, engineers, and <em>consultants.</em></h2>
        <div className="grantha-out-grid">
          {[
            { big: "94%", head: "Contracts benchmark", body: "Risk clause detection vs 87% avg for senior legal counsel. Speed: 8 min vs 3.4 hours. Cost: ₹8K vs ₹1.2-2.8L per contract." },
            { big: "91%", head: "Technical engineering", body: "Accuracy on spec tasks. On complex multi-code tasks (API+ASME+IS): 84% vs engineer 79%. 20× faster." },
            { big: "82%", head: "Claims analysis", body: "Recommended positions aligned with eventual arbitration outcome. Settlement valuation within ±15% in 71% of cases." },
          ].map((c, i) => (
            <div className="grantha-out-card" key={i} data-testid={`st-out-${i}`}>
              <div className="grantha-out-num">{c.big}</div>
              <div className="grantha-out-head">{c.head}</div>
              <div className="grantha-out-body">{c.body}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 20, maxWidth: 700 }}>Benchmarks are illustrative. Cognara is a decision-support tool — final judgment rests with qualified human professionals.</p>
      </div>
    </section>
  );
}

/* ═══════ S9 FAQ — grantha-faq ═══════ */
function Section9Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq" data-testid="st-faq">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h">Everything a <em>CTO or consulting head</em> wants to know.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`st-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a" data-testid={`st-faq-answer-${i}`}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 CTA — grantha-cta ═══════ */
function Section10Cta() {
  return (
    <section className="grantha-cta" data-testid="st-cta">
      <div className="container grantha-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>Meet Cognara</div>
        <h2 className="grantha-cta-h">Ask Cognara anything about your hardest project question — <em>get a cited, reasoned answer in under 60 seconds.</em></h2>
        <p className="grantha-cta-sub">Our "Ask Cognara" session is a free 30-minute live demo where you bring your hardest domain question — a contested claim, a confusing code requirement, a stuck schedule — and Cognara works through it with you. Not a canned demo. Your real question. Our real reasoning.</p>
        <div className="grantha-cta-row">
          <Link to="/contact?p=cognara" className="btn btn-primary grantha-cta-btn" data-testid="st-cta-ask">Ask Cognara a Question <span className="arrow">→</span></Link>
          <Link to="/resources" className="btn btn-ghost grantha-cta-ghost">Download Technical Whitepaper (PDF)</Link>
        </div>
      </div>
    </section>
  );
}
